import type {
  GetScoutServiceCostSummaryQuery,
  GetScoutTenantCostSummaryQuery,
  GetScoutTenantCostSummaryQueryVariables,
  ScoutCostPacingStatus,
} from "generated-graphql/graphql";

import { useQuery } from "@apollo/client";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { green, grey, orange, red } from "@mui/material/colors";
import { alpha, type Theme } from "@mui/material/styles";
import DonutChart from "components/Dashboard/DonutChart";
import { DataGridCell } from "components/DataGridCell";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import ScoutIcon from "components/ScoutIcon";
import { gql } from "generated-graphql";
import { OmnisearchGridColDef, type FilterKey } from "hooks/useOmnisearchDatagrid";
import { useMemo } from "react";
import { prettyPrintDateTime } from "util/dates";

const COST_CONTROL_REFRESH_INTERVAL_MS = 60_000;

const SCOUT_COST_FILTER_COLUMNS: FilterKey[] = [
  {
    key: "isDemo",
    header: "Demo",
    filterKeyType: "boolean",
  },
];

const GET_SCOUT_SERVICE_COST_SUMMARY = gql(`
  query GetScoutServiceCostSummary {
    getScoutServiceCostSummary {
      usage {
        currentUsageUsd
        limitUsd
        resetAt
        pacingStatus
        usagePercent
        expectedUsageUsd
        estimatedTotalUsd
      }
      totalActiveTenantsCount
      tenantsWithScoutCount
      tenantsAheadOfPaceCount
      tenantsCappedCount
    }
  }
`);

export const GET_SCOUT_TENANT_COST_SUMMARY = gql(`
  query GetScoutTenantCostSummary(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    getScoutTenantCostSummary(
      search: $search
      page: $page
      pageSize: $pageSize
      sort: $sort
    ) {
      count
      items {
        id
        tenantId
        tenant {
          id
          name
        }
        usage {
          currentUsageUsd
          limitUsd
          resetAt
          pacingStatus
          usagePercent
          expectedUsageUsd
          estimatedTotalUsd
        }
        isLimitOverridden
      }
    }
  }
`);

type ScoutTenantCostRow =
  GetScoutTenantCostSummaryQuery["getScoutTenantCostSummary"]["items"][number];

type ScoutServiceCostSummary = GetScoutServiceCostSummaryQuery["getScoutServiceCostSummary"];

type ScoutCostUsage = GetScoutServiceCostSummaryQuery["getScoutServiceCostSummary"]["usage"];

const TENANT_DISTRIBUTION_COLORS = {
  nonScout: grey[400],
  onPace: green[500],
  ahead: orange[500],
  capped: red[500],
} as const;

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatUsd(value: number): string {
  return currencyFormatter.format(value);
}

function getUsageBarPercent(currentUsageUsd: number, limitUsd: number): number {
  if (limitUsd <= 0) {
    return currentUsageUsd > 0 ? 100 : 0;
  }
  return Math.min(Math.max((currentUsageUsd / limitUsd) * 100, 0), 100);
}

function getPaceBarPercent(expectedUsageUsd: number, limitUsd: number): number {
  if (limitUsd <= 0) {
    return 0;
  }
  return Math.min(Math.max((expectedUsageUsd / limitUsd) * 100, 0), 100);
}

function getEstimatedTotalBarPercent(estimatedTotalUsd: number, limitUsd: number): number {
  if (limitUsd <= 0) {
    return estimatedTotalUsd > 0 ? 100 : 0;
  }
  return Math.min(Math.max((estimatedTotalUsd / limitUsd) * 100, 0), 100);
}

function getPacingFillColor(pacingStatus: ScoutCostPacingStatus, theme: Theme): string {
  if (pacingStatus === "AHEAD_OF_PACE") {
    return theme.palette.warning.main;
  }
  if (pacingStatus === "CAPPED") {
    return theme.palette.error.main;
  }
  return theme.palette.success.main;
}

function getPacingTrackTint(pacingStatus: ScoutCostPacingStatus, theme: Theme): string {
  if (pacingStatus === "AHEAD_OF_PACE") {
    return theme.palette.warning.light;
  }
  if (pacingStatus === "CAPPED") {
    return theme.palette.error.light;
  }
  return theme.palette.success.light;
}

function getPacingStatusLabel(pacingStatus: ScoutCostPacingStatus): string {
  if (pacingStatus === "AHEAD_OF_PACE") {
    return "Ahead of pace";
  }
  if (pacingStatus === "CAPPED") {
    return "Capped";
  }
  return "On pace";
}

function getPacingChipColor(
  pacingStatus: ScoutCostPacingStatus
): "default" | "success" | "warning" | "error" {
  if (pacingStatus === "AHEAD_OF_PACE") {
    return "warning";
  }
  if (pacingStatus === "CAPPED") {
    return "error";
  }
  return "success";
}

function getEstimatedZoneStripeBackground(stripeColor: string, gapColor: string): string {
  return `repeating-linear-gradient(45deg, ${stripeColor} 0, ${stripeColor} 2px, ${gapColor} 2px, ${gapColor} 6px)`;
}

function ScoutCostColumnHeader({
  label,
  tooltip,
  align = "left",
}: {
  label: string;
  tooltip: string;
  align?: "left" | "right";
}) {
  return (
    <Tooltip title={tooltip} placement="top">
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        justifyContent={align === "right" ? "flex-end" : "flex-start"}
        sx={{ width: "100%" }}
      >
        <Box component="span" sx={{ fontWeight: 500 }}>
          {label}
        </Box>
        <InfoOutlined sx={{ fontSize: 14, color: "text.secondary" }} />
      </Stack>
    </Tooltip>
  );
}

function ScoutCostUsageProgressBar({
  usage,
  height = 8,
}: {
  usage: ScoutCostUsage;
  height?: number;
}) {
  const theme = useTheme();
  const usagePercent = getUsageBarPercent(usage.currentUsageUsd, usage.limitUsd);
  const pacePercent = getPaceBarPercent(usage.expectedUsageUsd, usage.limitUsd);
  const estimatedTotalPercent = getEstimatedTotalBarPercent(
    usage.estimatedTotalUsd,
    usage.limitUsd
  );
  const fillColor = getPacingFillColor(usage.pacingStatus, theme);
  const trackTint = getPacingTrackTint(usage.pacingStatus, theme);
  const estimatedZoneGapColor = alpha(fillColor, theme.palette.mode === "dark" ? 0.16 : 0.1);
  const leftoverTint =
    theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[200];
  const showLeftoverZone = estimatedTotalPercent < 100;
  const showEstimatedZone = estimatedTotalPercent > usagePercent;

  return (
    <Tooltip
      title={`Spent ${formatUsd(usage.currentUsageUsd)} · Prorated target ${formatUsd(usage.expectedUsageUsd)} · Estimated total ${formatUsd(usage.estimatedTotalUsd)}`}
    >
      <Box
        component="span"
        aria-label={`${usagePercent.toFixed(0)}% of limit used, pace marker at ${pacePercent.toFixed(0)}%, estimated total at ${estimatedTotalPercent.toFixed(0)}%`}
        sx={{
          display: "block",
          width: "100%",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height,
            borderRadius: 1,
            bgcolor: trackTint,
            overflow: "hidden",
          }}
        >
          {showLeftoverZone ? (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${estimatedTotalPercent}%`,
                right: 0,
                bgcolor: leftoverTint,
                pointerEvents: "none",
              }}
            />
          ) : null}
          {showEstimatedZone ? (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${usagePercent}%`,
                width: `${estimatedTotalPercent - usagePercent}%`,
                backgroundImage: getEstimatedZoneStripeBackground(fillColor, estimatedZoneGapColor),
                pointerEvents: "none",
              }}
            />
          ) : null}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              width: `${usagePercent}%`,
              bgcolor: fillColor,
              borderRadius: 1,
            }}
          />
          {pacePercent > 0 ? (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${Math.min(pacePercent, 100)}%`,
                width: 0,
                borderLeft: "2px dashed",
                borderColor: theme.palette.text.primary,
                opacity: 0.55,
                transform: "translateX(-1px)",
                pointerEvents: "none",
              }}
            />
          ) : null}
          {estimatedTotalPercent > 0 ? (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${Math.min(estimatedTotalPercent, 100)}%`,
                width: 0,
                borderLeft: "2px dotted",
                borderColor: theme.palette.text.secondary,
                opacity: 0.75,
                transform: "translateX(-1px)",
                pointerEvents: "none",
              }}
            />
          ) : null}
        </Box>
      </Box>
    </Tooltip>
  );
}

function ScoutCostTenantCell({ row }: { row: ScoutTenantCostRow }) {
  return (
    <DataGridCell>
      <Stack spacing={0.75} sx={{ width: "100%", py: 0.5 }}>
        <Typography variant="body2" fontWeight={500} noWrap>
          {row.tenant.name}
        </Typography>
        <ScoutCostUsageProgressBar usage={row.usage} />
      </Stack>
    </DataGridCell>
  );
}

function ScoutCostTenantDistributionChart({
  summary,
  loading,
}: {
  summary: ScoutServiceCostSummary | undefined;
  loading: boolean;
}) {
  const seriesData = useMemo(() => {
    if (!summary) {
      return [];
    }

    const nonScoutCount = Math.max(
      0,
      summary.totalActiveTenantsCount - summary.tenantsWithScoutCount
    );
    const onPaceCount = Math.max(
      0,
      summary.tenantsWithScoutCount - summary.tenantsAheadOfPaceCount - summary.tenantsCappedCount
    );

    return [
      {
        id: "non-scout",
        value: nonScoutCount,
        label: "No Scout",
        color: TENANT_DISTRIBUTION_COLORS.nonScout,
      },
      {
        id: "on-pace",
        value: onPaceCount,
        label: "Scout on pace",
        color: TENANT_DISTRIBUTION_COLORS.onPace,
      },
      {
        id: "ahead",
        value: summary.tenantsAheadOfPaceCount,
        label: "Ahead of pace",
        color: TENANT_DISTRIBUTION_COLORS.ahead,
      },
      {
        id: "capped",
        value: summary.tenantsCappedCount,
        label: "Capped",
        color: TENANT_DISTRIBUTION_COLORS.capped,
      },
    ].filter((segment) => segment.value > 0);
  }, [summary]);

  const legendItems = useMemo(
    () =>
      seriesData.map((segment) => ({
        color: segment.color,
        tooltip: `${segment.label}: ${segment.value.toLocaleString()}`,
        value: (
          <Box display="flex" width="100%" justifyContent="space-between" gap={1}>
            <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {segment.label}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
              {segment.value.toLocaleString()}
            </Typography>
          </Box>
        ),
      })),
    [seriesData]
  );

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
          Active tenants
        </Typography>
        {loading && !summary ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <Skeleton variant="circular" width={140} height={140} />
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Skeleton width="80%" />
              <Skeleton width="70%" />
              <Skeleton width="75%" />
            </Stack>
          </Stack>
        ) : seriesData.length === 0 ? (
          <Typography variant="body2" color="text.disabled">
            No active tenants
          </Typography>
        ) : (
          <DonutChart
            seriesData={seriesData}
            label={summary?.totalActiveTenantsCount.toLocaleString() ?? "0"}
            subLabel="Active"
            containerHeight="10rem"
            legend={{
              direction: "column",
              position: "right",
            }}
            legendItems={legendItems}
            legendSx={{ minWidth: 160, maxWidth: 220 }}
            seriesOverrides={{
              innerRadius: 40,
              outerRadius: 70,
            }}
            justifyContent="start"
          />
        )}
      </CardContent>
    </Card>
  );
}

export function ScoutCostControl() {
  const { data, loading, error } = useQuery<GetScoutServiceCostSummaryQuery>(
    GET_SCOUT_SERVICE_COST_SUMMARY,
    {
      pollInterval: COST_CONTROL_REFRESH_INTERVAL_MS,
      fetchPolicy: "cache-and-network",
    }
  );

  const summary = data?.getScoutServiceCostSummary;
  const usage = summary?.usage;

  const columns: OmnisearchGridColDef<ScoutTenantCostRow>[] = useMemo(
    () => [
      {
        field: "tenant",
        headerName: "Tenant",
        flex: 2,
        minWidth: 240,
        sortable: true,
        topAligned: true,
        filterKeyType: "multipleTenant",
        renderHeader: () => (
          <ScoutCostColumnHeader
            label="Tenant"
            tooltip="Organization using Scout. The bar shows spend vs. the monthly limit; the dashed line marks the prorated target and the dotted line marks the estimated month-end total. Stripes between spent and the dotted line are projected spend; grey to the right is anticipated budget leftover."
          />
        ),
        valueGetter(_value, row) {
          return row.tenant.name;
        },
        renderCell({ row }) {
          return <ScoutCostTenantCell row={row} />;
        },
      },
      {
        field: "currentUsageUsd",
        headerName: "Used",
        flex: 0.55,
        minWidth: 100,
        sortable: true,
        align: "right",
        headerAlign: "right",
        renderHeader: () => (
          <ScoutCostColumnHeader
            label="Used"
            align="right"
            tooltip="Estimated Scout LLM spend for this tenant in the current UTC calendar month."
          />
        ),
        valueGetter(_value, row) {
          return row.usage.currentUsageUsd;
        },
        renderCell({ row }) {
          return formatUsd(row.usage.currentUsageUsd);
        },
      },
      {
        field: "expectedUsageUsd",
        headerName: "Prorated Target",
        flex: 0.6,
        minWidth: 120,
        sortable: true,
        align: "right",
        headerAlign: "right",
        renderHeader: () => (
          <ScoutCostColumnHeader
            label="Prorated Target"
            align="right"
            tooltip="Expected spend by today if usage were spread evenly across the month (limit × elapsed fraction of the UTC month). Shown as the dashed line on the progress bar."
          />
        ),
        valueGetter(_value, row) {
          return row.usage.expectedUsageUsd;
        },
        renderCell({ row }) {
          return formatUsd(row.usage.expectedUsageUsd);
        },
      },
      {
        field: "estimatedTotalUsd",
        headerName: "Estimated Total",
        flex: 0.65,
        minWidth: 120,
        sortable: true,
        align: "right",
        headerAlign: "right",
        renderHeader: () => (
          <ScoutCostColumnHeader
            label="Estimated Total"
            align="right"
            tooltip="Projected month-end spend if the current daily rate continues through the end of the month."
          />
        ),
        valueGetter(_value, row) {
          return row.usage.estimatedTotalUsd;
        },
        renderCell({ row }) {
          return formatUsd(row.usage.estimatedTotalUsd);
        },
      },
      {
        field: "limitUsd",
        headerName: "Limit",
        flex: 0.55,
        minWidth: 100,
        sortable: true,
        align: "right",
        headerAlign: "right",
        renderHeader: () => (
          <ScoutCostColumnHeader
            label="Limit"
            align="right"
            tooltip="Maximum monthly Scout spend allowed for this tenant before usage is capped."
          />
        ),
        valueGetter(_value, row) {
          return row.usage.limitUsd;
        },
        renderCell({ row }) {
          return formatUsd(row.usage.limitUsd);
        },
      },
      {
        field: "pacingStatus",
        headerName: "Status",
        flex: 0.65,
        minWidth: 130,
        sortable: true,
        renderHeader: () => (
          <ScoutCostColumnHeader
            label="Status"
            tooltip="On pace (at or below prorated target), Ahead of pace (above prorated target but under limit), or Capped (at or over the limit)."
          />
        ),
        valueGetter(_value, row) {
          return row.usage.pacingStatus;
        },
        renderCell({ row }) {
          return (
            <Chip
              size="small"
              label={getPacingStatusLabel(row.usage.pacingStatus)}
              color={getPacingChipColor(row.usage.pacingStatus)}
            />
          );
        },
      },
    ],
    []
  );

  return (
    <Stack spacing={3} sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: "column", md: "row" }} alignItems="center" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1.5} flex={1}>
          <ScoutIcon sx={{ color: "encampBlue.main", width: 36, height: 36 }} />
          <Box>
            <Typography variant="h4" fontWeight={600}>
              Scout Cost Control
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monthly Scout LLM spend against service and tenant limits (UTC calendar month).
            </Typography>
          </Box>
        </Stack>
        {usage ? (
          <Typography variant="body2" color="text.secondary">
            Resets {prettyPrintDateTime(usage.resetAt)} UTC
          </Typography>
        ) : null}
      </Stack>

      {error ? (
        <Typography color="error">Failed to load service cost summary: {error.message}</Typography>
      ) : null}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ height: "100%" }}>
            <CardContent>
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                  spacing={2}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    Service spend
                  </Typography>
                  {usage ? (
                    <Typography variant="body2" color="text.secondary">
                      {formatUsd(usage.currentUsageUsd)} / {formatUsd(usage.limitUsd)}
                    </Typography>
                  ) : (
                    <Skeleton width={120} />
                  )}
                </Stack>
                {usage ? (
                  <ScoutCostUsageProgressBar usage={usage} height={12} />
                ) : (
                  <Skeleton variant="rectangular" height={12} sx={{ borderRadius: 1 }} />
                )}
                {usage ? (
                  <Typography variant="caption" color="text.secondary">
                    {getPacingStatusLabel(usage.pacingStatus)} · Prorated target{" "}
                    {formatUsd(usage.expectedUsageUsd)} · Estimated total{" "}
                    {formatUsd(usage.estimatedTotalUsd)}
                  </Typography>
                ) : null}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ScoutCostTenantDistributionChart summary={summary} loading={loading && !summary} />
        </Grid>
      </Grid>

      <OmnisearchDataGrid<
        GetScoutTenantCostSummaryQuery,
        GetScoutTenantCostSummaryQueryVariables,
        ScoutTenantCostRow
      >
        persistenceKey="staffScoutCostControlTenants"
        isURLDriven
        withPadding={false}
        dataQuery={GET_SCOUT_TENANT_COST_SUMMARY}
        getItems={(queryData) => queryData.getScoutTenantCostSummary.items}
        getCount={(queryData) => queryData.getScoutTenantCostSummary.count}
        getRowId={(row) => row.id}
        rowHeight="auto"
        initialSortModel={[{ field: "currentUsageUsd", sort: "desc" }]}
        columns={columns}
        additionalFilterColumns={SCOUT_COST_FILTER_COLUMNS}
      />
    </Stack>
  );
}
